<template>
    <!-- *********************************************
    *                  Account Block                 *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="account-balance"
    :applyBorder="false"
    :title="t('cards.charge.mcx.step1.dividerAccount')"/>
    <!-- Accounts Dropdown -->
    <dropdown-account
    id="originAccount"
    :params="originAccountParams"
    :options="getAccountsBalances"
    :selectedOption="formData.originAccount"
    @clicked="[emit('updateFormData', { field: 'originAccount', inputValue: $event }), clearFieldError('originAccountParams')]"/>
    <!-- *********************************************
    *                    Card Block                  *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="credit-card"
    :title="t('cards.charge.mcx.step1.dividerCard')"/>
    <!-- CARDS DROPDOWN -->
    <div class="dropdown-card-block">
        <dropdown-cards
        id="originCard"
        :params="originCardParams"
        :options="getCards"
        :selectedOption="formData.originCard"
        @clicked="[emit('updateFormData', { field: 'originCard', inputValue: $event }), clearFieldError('originCardParams')]"/>
        <!-- DETAILS CONTAINER -->
        <container-collapsible
        startClosed
        doubleColumn
        removeBorder
        :title="t('textBlocks.showDetails')"
        :openTitle="t('textBlocks.hideDetails')">
            <!-- Text Block -->
            <text-block-generic
            v-for="(entry, index) in commonCardDetailParser(getDisplayCardDetails)"
            :key="index.key"
            :params="entry"
            :applyEllipsis="entry.applyEllipsis"
            :mask="entry.mask"/>
        </container-collapsible>
    </div>
    <!-- *********************************************
    *                  Amount Block                  *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="bills"
    :title="t('cards.charge.mcx.step1.dividerAmount')"/>
    <!-- Amount Input -->
    <input-amount
    id="amount"
    :params="amountParams"
    :inputValue="formData.amount"
    @onBlur="validateExchange"
    @input="[emit('updateFormData', {field: 'amount', inputValue: $event}), clearFieldError('amountParams')]"/>

</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InputAmount from '@/components/generic/inputs/Amount'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import ContainerCollapsible from '@/components/generic/containers/Collapsible'
import DropdownCards from '@/components/generic/dropdowns/cards/Card'

/* Vue */
import { ref, watch, computed, onMounted } from 'vue'

/* Assets */
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { commonCardDetailParser } from '@/assets/scripts/parsers/cards'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateFormData', 'formErrorsCleared', 'resetShowMandatoryErrorsProp', 'scrollTo', 'exchangeAmountButtonClicked'])

/* props received
@clearFormErrors { Boolean } - Prop to remove of form errors
@formData { Object } - object with info for our current form
@mandatoryList { Array } Array of mandatory keys corresponding with our form
@showMandatoryErrors { Boolean } Prop to trigger display of mandatory errors
*/
const props = defineProps({
    getCards: Array,
    getAccountsBalances: Array,
    clearFormErrors: Boolean,
    formData: Object,
    mandatoryList: Array,
    showMandatoryErrors: Boolean
})
/* Data */

const originAccountParams = ref({
    labelText: t('cards.charge.mcx.step1.account'),
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const originCardParams = ref({
	labelText: t('cards.charge.mcx.step1.card'),
	mandatory: true,
	headerType: 'balance-header',
    withBorder: true
})

const getDisplayCardDetails = computed(() => {
	return props.getCards.find(card => card.id === props.formData.originCard)
})

const amountParams = ref({
    labelText: t('cards.charge.mcx.step1.chargeAmount'),
    mandatory: true,
    currency: props.formData.currency,
    errorState: false,
    errorMessage: null
})

const validateExchange = () => {
    if (!!props.formData.amount && props.formData.amount <= 0) {
        amountParams.value.errorState = true
        amountParams.value.errorMessage = t('invalidAmount')
    }
}

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	originCardParams: originCardParams.value,
    originAccountParams: originAccountParams.value,
    amountParams: amountParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

	if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

onMounted(() => {
    emit('scrollTo', 0)
})
</script>
<style lang="scss" scoped>
.reuse-button-wrapper {
    width: 500px;
    max-width: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;

    .reuse-button {
        margin-bottom: 24px;
    }
}
.dropdown-card-block {
	width: 100%;
}

.exchange-wrapper {
    .text-blocks-wrapper {
            margin-bottom: 16px;
        }
}
</style>
